'use client';

import { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, HelpCircle } from 'lucide-react';

interface TourStep {
  target: string;
  title: string;
  description: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

const DEFAULT_STEPS: TourStep[] = [
  { target: '[data-viewer]', title: 'Interactive 3D Viewer', description: 'Rotate, zoom, and explore the product in 3D. Use your mouse or touch to interact.', position: 'right' },
  { target: '[data-sidebar]', title: 'Configuration Options', description: 'Choose colors, materials, and features. Your selections update the 3D view in real-time.', position: 'left' },
  { target: '[data-share]', title: 'Share Configuration', description: 'Save and share your custom configuration with others via a unique link.', position: 'bottom' },
];

export function ProductTour() {
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const seen = localStorage.getItem('c3d_tour_seen');
    if (!seen) {
      const timer = setTimeout(() => setActive(true), 2000);
      return () => clearTimeout(timer);
    } else {
      setDismissed(true);
    }
  }, []);

  const close = useCallback(() => {
    setActive(false);
    setDismissed(true);
    localStorage.setItem('c3d_tour_seen', '1');
  }, []);

  const currentStep = DEFAULT_STEPS[step];

  function next() {
    if (step < DEFAULT_STEPS.length - 1) setStep(step + 1);
    else close();
  }

  function prev() {
    if (step > 0) setStep(step - 1);
  }

  if (!active) {
    return dismissed ? (
      <button
        onClick={() => { setActive(true); setStep(0); }}
        className="fixed bottom-3 right-3 z-40 p-2 rounded-full bg-blue-500 text-white shadow-lg hover:bg-blue-600 transition-colors"
        title="Product tour"
      >
        <HelpCircle className="h-4 w-4" />
      </button>
    ) : null;
  }

  const targetEl = typeof document !== 'undefined' ? document.querySelector(currentStep.target) : null;
  const rect = targetEl?.getBoundingClientRect();

  const tooltipStyle: React.CSSProperties = {
    position: 'fixed',
    zIndex: 60,
  };

  if (rect) {
    const pos = currentStep.position || 'bottom';
    if (pos === 'right') {
      tooltipStyle.left = Math.min(rect.right + 16, window.innerWidth - 340);
      tooltipStyle.top = rect.top + rect.height / 2 - 60;
    } else if (pos === 'left') {
      tooltipStyle.right = window.innerWidth - rect.left + 16;
      tooltipStyle.top = rect.top + rect.height / 2 - 60;
    } else if (pos === 'top') {
      tooltipStyle.left = rect.left + rect.width / 2 - 150;
      tooltipStyle.bottom = window.innerHeight - rect.top + 16;
    } else {
      tooltipStyle.left = rect.left + rect.width / 2 - 150;
      tooltipStyle.top = rect.bottom + 16;
    }
  } else {
    tooltipStyle.top = '50%';
    tooltipStyle.left = '50%';
    tooltipStyle.transform = 'translate(-50%, -50%)';
  }

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40 pointer-events-none" />

      {rect && (
        <div
          className="fixed z-[55] rounded-lg"
          style={{
            left: rect.left - 4,
            top: rect.top - 4,
            width: rect.width + 8,
            height: rect.height + 8,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.4)',
            pointerEvents: 'none',
          }}
        />
      )}

      <div style={tooltipStyle} className="w-[300px] pointer-events-auto">
        <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-2xl border border-zinc-200 dark:border-zinc-800 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-100 dark:border-zinc-800">
            <span className="text-xs text-zinc-400">Step {step + 1} of {DEFAULT_STEPS.length}</span>
            <button onClick={close} className="p-0.5 text-zinc-400 hover:text-zinc-600 transition-colors">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="px-4 py-3">
            <h4 className="text-sm font-semibold mb-1">{currentStep.title}</h4>
            <p className="text-xs text-zinc-500 leading-relaxed">{currentStep.description}</p>
          </div>
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900">
            <div className="flex gap-1">
              {DEFAULT_STEPS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 w-1.5 rounded-full transition-colors ${i === step ? 'bg-blue-500' : 'bg-zinc-300 dark:bg-zinc-700'}`}
                />
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              {step > 0 && (
                <button onClick={prev} className="flex items-center gap-0.5 text-xs text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors">
                  <ChevronLeft className="h-3 w-3" />
                  Back
                </button>
              )}
              <button
                onClick={next}
                className="flex items-center gap-0.5 text-xs font-medium bg-blue-500 text-white px-3 py-1 rounded-md hover:bg-blue-600 transition-colors"
              >
                {step < DEFAULT_STEPS.length - 1 ? 'Next' : 'Done'}
                {step < DEFAULT_STEPS.length - 1 && <ChevronRight className="h-3 w-3" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
